import { SceneRenderer } from './SceneRenderer';
import { Context } from '../context/Context';
import { DepthMode, RenderMode, RenderSettings } from './index';
import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { Scene } from '../scene/Scene';
import { Camera } from '../camera';
import { InstanceManager } from './InstanceManager';
import { RenderObject } from '../object/RenderObject';

export class DepthMapRenderer extends SceneRenderer {
  private ctx!: Context;

  /** When set, instanced objects are drawn with instanced calls. */
  public instanceManager?: InstanceManager;

  /**
   * Camera position used for LOD distance checks.  For shadow passes this
   * should be the *main* camera position, not the light-space cascade camera.
   */
  public mainCameraPosition?: ReadonlyVec3;

  /** LOD distance multiplier — mirrors GraphicsSettings.lodDistanceScale. */
  public lodDistanceScale = 1.0;

  /**
   * When true, force every visible instance through the lowest-detail
   * non-billboard LOD (via `InstanceGroup.getShadowLodIndex()`), bypassing
   * the camera-distance LOD partition. Billboards are intentionally skipped
   * because their cross-plane silhouette casts cardboard-shaped shadows.
   * Groups with no decimated LOD registered fall back to the base mesh.
   * When false, casters render with the standard distance-partitioned set
   * (base mesh + each registered LOD at its distance band).
   */
  public useShadowLod = false;

  /**
   * Controls which casters are rendered in this depth pass:
   *   'all'     — clear + render instanced groups + non-instanced objects (default)
   *   'static'  — clear + render instanced groups only (all instanced objects are static)
   *   'dynamic' — NO clear; skip instanced groups, render non-instanced objects only
   * The 'dynamic' path skips the clear so the blitted static depth is preserved.
   */
  public shadowCasterFilter: 'all' | 'static' | 'dynamic' = 'all';

  /**
   * Skip *animated* non-instanced casters whose bounding-sphere center is
   * farther than this distance from `mainCameraPosition`. Set per-cascade by
   * the shadow renderer to drop tiny far-away animated-model shadows that
   * cost full skinning per pass but are invisibly small on-screen.
   *
   * Static non-instanced casters (e.g. terrain — `TerrainMaterial` is excluded
   * from `InstanceManager.isInstanceable`) are never filtered here; far-cascade
   * coverage of mountain-scale terrain depends on them being drawn. Defaults
   * to Infinity (no filtering).
   */
  public maxDynamicShadowDistance = Infinity;

  constructor(
    public depthMode: DepthMode,
    public depthPoint?: vec3 | number[],
  ) {
    super();

    if (this.depthMode == DepthMode.PointDistance && !this.depthPoint) {
      throw new Error(`Must set depthPoint when using DepthMode.PointDistance`);
    }
  }

  public init(ctx: Context): void {
    this.ctx = ctx;
  }

  public destroy(): void {}

  public render(scene: Scene, camera: Camera, buffer: WebGLFramebuffer): void {
    let ctx = this.ctx;
    let gl = this.ctx.gl;

    gl.bindFramebuffer(gl.FRAMEBUFFER, buffer);

    gl.viewport(0, 0, camera.dimensions.width, camera.dimensions.height);

    // 'dynamic' pass preserves the blitted static depth — do not clear.
    if (this.shadowCasterFilter !== 'dynamic') {
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    }

    // Disable culling so single-sided floors/walls always write depth (no sun
    // leaking through at grazing angles), while closed casters naturally end
    // up with their front (lit) face recorded by the depth test — avoiding
    // the peter-panning that front-face culling causes under thick objects.
    gl.disable(gl.CULL_FACE);
    // Slope-scale polygon offset handles depth-buffer texel quantization on
    // caster triangles. Smooth-normal vs flat-triangle acne (the main acne
    // mechanism on the analytic-normal terrain) is killed receiver-side in
    // `csm_shadow.glsl`, which derives a geometric face normal from
    // `dFdx`/`dFdy` of worldPos so the bias is constant across each triangle.
    // 2.0/4.0 are the historical pre-no-cull values.
    gl.enable(gl.POLYGON_OFFSET_FILL);
    gl.polygonOffset(2.0, 4.0);

    let objects = scene.objectManager.getAllVisibleObjects(RenderMode.Depth, camera.frustum);

    if (this.instanceManager) {
      const { instanceGroups, nonInstancedObjects } = this.instanceManager.prepareFrame(
        gl,
        objects,
        RenderMode.Depth,
        this.mainCameraPosition,
        this.lodDistanceScale,
      );

      // 'dynamic' skips the instanced draw — static depth was blitted in from the cache.
      if (this.shadowCasterFilter !== 'dynamic') {
        const shader = ctx.engine.shaderManager.getDepthShader('INSTANCED');
        ctx.setShader(shader);
        // Point-light cube shadow maps need each instanced caster fragment to
        // write linear distance-to-light into fragColor (sampled later in
        // pcf_shadow_cube). For spot/directional, depthPoint.w stays 0 so the
        // shader writes a dummy color and relies on the hardware depth buffer.
        const dp = this.depthMode === DepthMode.PointDistance && this.depthPoint
          ? [this.depthPoint[0], this.depthPoint[1], this.depthPoint[2], 1.0]
          : [0, 0, 0, 0];
        shader.uniformsContainer
          .setViewMatrix(camera.modelViewMatrix)
          .setProjectionMatrix(camera.projectionMatrix)
          .setDepthPoint(dp);
        shader.validate();

        for (const group of instanceGroups) {
          // Far-billboard groups (base mesh = cross-billboard) are excluded from
          // all shadow passes — their silhouette produces cardboard-shaped shadows.
          if (!group.castsShadows) continue;
          if (this.useShadowLod && group.drawAllAtShadowMesh(gl)) {
            // Forced shadow path — every visible instance rendered through the
            // dedicated shadow mesh (typically the decimated tree). Skips the
            // billboard tier so shadows don't end up cardboard-shaped.
            continue;
          }
          // Standard distance-partitioned path — base mesh + each registered LOD.
          // Billboard LODs are always skipped: cross-plane silhouettes cast
          // cardboard-shaped shadows that are visibly wrong.
          group.draw(gl);
          for (let lod = 0; lod < group.lodLevelCount; lod++) {
            if (!group.isLodBillboard(lod)) group.drawLod(gl, lod);
          }
        }

        // 'static' filter stops here — skip non-instanced (dynamic) objects.
        if (this.shadowCasterFilter === 'static') {
          gl.enable(gl.CULL_FACE);
          gl.disable(gl.POLYGON_OFFSET_FILL);
          gl.polygonOffset(0.0, 0.0);
          gl.bindFramebuffer(gl.FRAMEBUFFER, null);
          return;
        }
      }

      objects = nonInstancedObjects;
    }

    let renderSettings: RenderSettings = {
      mode: RenderMode.Depth,
      lights: [],
      objects: objects,
      depthMode: this.depthMode,
      depthPoint: this.depthPoint,
    };

    const maxDistSq =
      isFinite(this.maxDynamicShadowDistance) && this.mainCameraPosition
        ? this.maxDynamicShadowDistance * this.maxDynamicShadowDistance
        : Infinity;
    const camPos = this.mainCameraPosition;

    for (let obj of objects) {
      // Distance cap targets animated models specifically — full skinning is
      // expensive and tiny far-away animated casters aren't worth it. Static
      // non-instanced casters (terrain) must NOT be filtered: cascade 3
      // coverage of km-scale mountain shadows depends on them.
      if (
        isFinite(maxDistSq) &&
        camPos &&
        obj instanceof RenderObject &&
        obj.isAnimated &&
        obj.bounds
      ) {
        const p = obj.bounds.boundingSphere.position;
        const dx = p[0] - camPos[0];
        const dy = p[1] - camPos[1];
        const dz = p[2] - camPos[2];
        if (dx * dx + dy * dy + dz * dz > maxDistSq) continue;
      }
      obj.render(ctx, camera, renderSettings);
    }

    // Restore default GL state for subsequent passes.
    gl.enable(gl.CULL_FACE);
    gl.disable(gl.POLYGON_OFFSET_FILL);
    gl.polygonOffset(0.0, 0.0);

    // Restore the default framebuffer so the next caller doesn't inherit our
    // shadow/depth FBO (which would feedback-loop if its attached texture is
    // sampled in the same draw).
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }
}
